<template>
  <div class="grid crud-demo">
    <div class="col-12">
      <div class="card">
        <Toast />
        <Toolbar class="mb-4">
          <template #start>
            <div class="my-2">
              <Button
                label="New"
                icon="pi pi-plus"
                class="p-button-success mr-2"
                @click="openNew"
              />
              <Button
                label="Delete"
                icon="pi pi-trash"
                class="p-button-danger"
                :disabled="!selectedQuestions || !selectedQuestions.length"
                @click="deleteSelectedDialog = true"
              />
            </div>
          </template>
          <template #end>
            <Button
              label="Import"
              icon="pi pi-upload"
              class="p-button-secondary mr-2"
              @click="importDialog = true"
            />
            <Button
              label="Export"
              icon="pi pi-download"
              class="p-button-help"
              @click="exportDialog = true"
            />
          </template>
        </Toolbar>

        <DataTable
          v-model:selection="selectedQuestions"
          :value="questions"
          data-key="id"
          lazy
          :paginator="true"
          :rows="rows"
          :first="first"
          :total-records="total"
          :loading="loading"
          :sort-field="sortField"
          :sort-order="sortOrder"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rows-per-page-options="[5, 10, 25]"
          current-page-report-template="Showing {first} to {last} of {totalRecords} questions"
          @page="onPage"
          @sort="onSort"
        >
          <template #header>
            <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
              <h5 class="m-0">
                Manage Questions
              </h5>
              <IconField class="block mt-2 md:mt-0">
                <InputIcon class="pi pi-search" />
                <InputText
                  v-model="searchQuery"
                  placeholder="Search..."
                  @input="onSearch"
                />
              </IconField>
            </div>
          </template>

          <Column
            selection-mode="multiple"
            header-style="width: 3rem"
          />
          <Column
            field="id"
            header="ID"
            :sortable="true"
          >
            <template #body="slotProps">
              <span class="p-column-title">ID</span>
              {{ slotProps.data.id }}
            </template>
          </Column>
          <Column
            field="question"
            header="Question"
            :sortable="true"
          >
            <template #body="slotProps">
              <span class="p-column-title">Question</span>
              {{ slotProps.data.question }}
            </template>
          </Column>
          <Column
            field="answer"
            header="Answer"
            :sortable="true"
          >
            <template #body="slotProps">
              <span class="p-column-title">Answer</span>
              {{ slotProps.data.answer }}
            </template>
          </Column>
          <Column
            field="group_id"
            header="Group"
            :sortable="true"
          >
            <template #body="slotProps">
              <span class="p-column-title">Group</span>
              {{ groupsById[slotProps.data.group_id] || '—' }}
            </template>
          </Column>
          <Column
            field="correct_answer_cnt"
            header="Correct answer counter"
            :sortable="true"
          >
            <template #body="slotProps">
              <span class="p-column-title">Correct answer counter</span>
              {{ slotProps.data.correct_answer_cnt }}
            </template>
          </Column>
          <Column>
            <template #body="slotProps">
              <Button
                icon="pi pi-pencil"
                class="p-button-rounded p-button-success mr-2"
                @click="editQuestion(slotProps.data)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-warn"
                @click="confirmDeleteQuestion(slotProps.data)"
              />
            </template>
          </Column>
        </DataTable>

        <QuestionFormDialog
          v-model:visible="formDialog"
          :question="editingQuestion"
          @saved="onSaved"
        />

        <ConfirmDialog
          v-model:visible="deleteDialog"
          message="Are you sure you want to delete this question?"
          @confirm="deleteQuestion"
        />

        <ConfirmDialog
          v-model:visible="deleteSelectedDialog"
          message="Are you sure you want to delete the selected questions?"
          @confirm="deleteSelectedQuestions"
        />

        <QuestionExportDialog v-model:visible="exportDialog" />

        <QuestionImportDialog
          v-model:visible="importDialog"
          @imported="onSaved"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import QuestionFormDialog from '../components/questions/QuestionFormDialog.vue'
import QuestionExportDialog from '../components/questions/QuestionExportDialog.vue'
import QuestionImportDialog from '../components/questions/QuestionImportDialog.vue'

const store = useStore()
const toast = useToast()

const questions = computed(() => store.state.questions.items)
const total = computed(() => store.state.questions.total)
const loading = computed(() => store.state.questions.loading)
const groupsById = computed(() =>
  Object.fromEntries(store.state.groups.items.map(g => [g.id, g.title]))
)

const selectedQuestions = ref(null)

const formDialog = ref(false)
const editingQuestion = ref(null)
const deleteDialog = ref(false)
const questionToDelete = ref(null)
const deleteSelectedDialog = ref(false)
const exportDialog = ref(false)
const importDialog = ref(false)

const rows = ref(10)
const first = ref(0)
const sortField = ref('id')
const sortOrder = ref(1)
const searchQuery = ref('')
let searchTimer = null

onMounted(() => {
  loadPage()
  store.dispatch('groups/getGroups')
})

function loadPage () {
  const page = Math.floor(first.value / rows.value) + 1
  return store.dispatch('questions/getQuestions', {
    page,
    limit: rows.value,
    search: searchQuery.value,
    sort: sortField.value,
    order: sortOrder.value === -1 ? 'desc' : 'asc'
  })
}

function openNew () {
  editingQuestion.value = null
  formDialog.value = true
}

function editQuestion (question) {
  editingQuestion.value = question
  formDialog.value = true
}

// A save or import may create new groups, so refresh both groups and the page.
async function onSaved () {
  await store.dispatch('groups/getGroups')
  loadPage()
}

function confirmDeleteQuestion (question) {
  questionToDelete.value = question
  deleteDialog.value = true
}

function deleteQuestion () {
  store.dispatch('questions/deleteQuestion', questionToDelete.value.id)
    .then(() => {
      deleteDialog.value = false
      questionToDelete.value = null
      loadPage()
      toast.add({ severity: 'success', summary: 'Successful', detail: 'Question Deleted', life: 3000 })
    })
    .catch(err => {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete question', life: 3000 })
      console.error('Delete error:', err)
    })
}

function deleteSelectedQuestions () {
  const ids = selectedQuestions.value.map(q => q.id)

  store.dispatch('questions/deleteSelectedQuestions', ids)
    .then(() => {
      deleteSelectedDialog.value = false
      selectedQuestions.value = null
      loadPage()
      toast.add({ severity: 'success', summary: 'Successful', detail: 'Questions Deleted', life: 3000 })
    })
    .catch(err => {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete questions', life: 3000 })
      console.error('Delete multiple error:', err)
    })
}

function onPage (event) {
  first.value = event.first
  rows.value = event.rows
  loadPage()
}

function onSort (event) {
  sortField.value = event.sortField || 'id'
  sortOrder.value = event.sortOrder || 1
  first.value = 0
  loadPage()
}

function onSearch () {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    first.value = 0
    loadPage()
  }, 350)
}
</script>
